import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import {
  getIncentives,
  getIncentiveById,
  createIncentive as createIncentiveApi,
  updateIncentive as updateIncentiveApi,
  removeIncentiveApi,
  extendIncentive as extendIncentiveApi,
} from '../api/incentive';
import type { Incentive, IncentiveFilters, CreateIncentiveForm } from '../types/incentive';

interface IncentiveState {
  incentives: Incentive[];
  selectedIncentiveDetails: Incentive | null;
  selectedIncentiveIds: string[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  filters: IncentiveFilters;
  totalEntries: number;
}

const initialState: IncentiveState = {
  incentives: [],
  selectedIncentiveDetails: null,
  selectedIncentiveIds: [],
  loading: false,
  error: null,
  successMessage: null,
  filters: {
    search: '',
    location: 'All Locations',
    product: 'All Products',
    distributorType: 'All Types',
    status: 'Status',
    page: 1,
    limit: 25,
  },
  totalEntries: 0,
};

export const fetchIncentives = createAsyncThunk(
  'incentive/fetchIncentives',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { incentive: IncentiveState };
      const res = await getIncentives(state.incentive.filters);
      return res;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch incentives');
    }
  }
);

export const fetchIncentiveDetails = createAsyncThunk(
  'incentive/fetchIncentiveDetails',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await getIncentiveById(id);
      return res;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch incentive details');
    }
  }
);

export const addIncentive = createAsyncThunk(
  'incentive/addIncentive',
  async (formData: CreateIncentiveForm, { rejectWithValue }) => {
    try {
      const newIncentive = await createIncentiveApi(formData);
      return newIncentive;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to create incentive');
    }
  }
);

export const editIncentive = createAsyncThunk(
  'incentive/editIncentive',
  async (formData: CreateIncentiveForm, { rejectWithValue }) => {
    try {
      const updated = await updateIncentiveApi(formData);
      return updated;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update incentive');
    }
  }
);

export const deleteIncentive = createAsyncThunk(
  'incentive/deleteIncentive',
  async (id: string, { rejectWithValue }) => {
    try {
      await removeIncentiveApi(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to remove incentive');
    }
  }
);

export const extendIncentiveValidity = createAsyncThunk(
  'incentive/extendValidity',
  async (
    { id, days, remarks }: { id: string; days: number; remarks?: string },
    { rejectWithValue }
  ) => {
    try {
      const updated = await extendIncentiveApi(id, days, remarks);
      return { updated, days };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to extend validity');
    }
  }
);

const incentiveSlice = createSlice({
  name: 'incentive',
  initialState,
  reducers: {
    setSearchFilter: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },
    setLocationFilter: (state, action: PayloadAction<string>) => {
      state.filters.location = action.payload;
      state.filters.page = 1;
    },
    setProductFilter: (state, action: PayloadAction<string>) => {
      state.filters.product = action.payload;
      state.filters.page = 1;
    },
    setDistributorTypeFilter: (state, action: PayloadAction<string>) => {
      state.filters.distributorType = action.payload;
      state.filters.page = 1;
    },
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.filters.status = action.payload;
      state.filters.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.filters.limit = action.payload;
      state.filters.page = 1;
    },
    toggleSelectIncentive: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedIncentiveIds.includes(id)) {
        state.selectedIncentiveIds = state.selectedIncentiveIds.filter((item) => item !== id);
      } else {
        state.selectedIncentiveIds.push(id);
      }
    },
    toggleSelectAllIncentives: (state) => {
      if (state.selectedIncentiveIds.length === state.incentives.length && state.incentives.length > 0) {
        state.selectedIncentiveIds = [];
      } else {
        state.selectedIncentiveIds = state.incentives.map((i) => i.id);
      }
    },
    clearSelectedIncentives: (state) => {
      state.selectedIncentiveIds = [];
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.selectedIncentiveIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchIncentives
      .addCase(fetchIncentives.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIncentives.fulfilled, (state, action) => {
        state.loading = false;
        state.incentives = action.payload.data;
        state.totalEntries = action.payload.total ?? action.payload.data.length;
      })
      .addCase(fetchIncentives.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchIncentiveDetails
      .addCase(fetchIncentiveDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchIncentiveDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedIncentiveDetails = action.payload;
      })
      .addCase(fetchIncentiveDetails.rejected, (state) => {
        state.loading = false;
      })
      // addIncentive
      .addCase(addIncentive.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addIncentive.fulfilled, (state, action) => {
        state.loading = false;
        state.incentives.unshift(action.payload);
        state.totalEntries += 1;
        state.successMessage = `Incentive program "${action.payload.name}" created successfully!`;
      })
      .addCase(addIncentive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // editIncentive
      .addCase(editIncentive.pending, (state) => {
        state.loading = true;
      })
      .addCase(editIncentive.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedIncentiveDetails = action.payload;
        state.successMessage = `Incentive program "${action.payload.name}" updated successfully!`;
      })
      .addCase(editIncentive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteIncentive
      .addCase(deleteIncentive.fulfilled, (state, action) => {
        state.incentives = state.incentives.filter((i) => i.id !== action.payload);
        state.successMessage = `Incentive program removed successfully.`;
      })
      // extendValidity
      .addCase(extendIncentiveValidity.fulfilled, (state, action) => {
        const { updated, days } = action.payload;
        state.successMessage = `Extended validity by ${days} days for incentive "${updated.name}".`;
        const idx = state.incentives.findIndex((i) => i.id === updated.id);
        if (idx !== -1) {
          state.incentives[idx] = updated;
        }
      });
  },
});

export const {
  setSearchFilter,
  setLocationFilter,
  setProductFilter,
  setDistributorTypeFilter,
  setStatusFilter,
  setPage,
  setLimit,
  toggleSelectIncentive,
  toggleSelectAllIncentives,
  clearSelectedIncentives,
  clearSuccessMessage,
  resetFilters,
} = incentiveSlice.actions;

export default incentiveSlice.reducer;
